/**
 * Customer-facing horizontal lifecycle for a remediation task.
 * Backend statuses stay as-is; this only maps them onto simpler milestones
 * in the detail drawer.
 */

export const LIFECYCLE_STEPS = [
  {
    id: "validation",
    label: "Validation",
    statuses: [
      "REQUESTED",
      "ASSUMING_ROLE",
      "CHECKING_PERMS",
      "PRECHECK_RUNNING",
      "PLAN_BUILDING",
    ],
  },
  {
    id: "awaiting_approval",
    label: "Awaiting Approval",
    statuses: ["DRY_RUN_READY", "NEEDS_INPUTS", "PERMS_DENIED"],
  },
  { id: "scheduled", label: "Scheduled", statuses: ["APPROVED"] },
  { id: "running", label: "In Progress", statuses: ["IN_PROGRESS"] },
  { id: "complete", label: "Completed", statuses: ["COMPLETED", "ALREADY_REMEDIATED"] },
];

const TERMINAL_CANCELLED = new Set(["CANCELLED", "ABANDONED"]);
const TERMINAL_FAILED = new Set(["FAILED", "VERIFY_FAILED", "PRECHECK_FAILED"]);

/**
 * Backend status → the five customer stepper pills (stakeholder mapping).
 * Several backend states share a pill: the later timestamp wins.
 * Awaiting Approval merges NEEDS_INPUTS, DRY_RUN_READY, and PERMS_DENIED.
 * Last pill: COMPLETED/succeeded, ALREADY_REMEDIATED, and FAILED/PRECHECK_FAILED
 * all land on the Completed/Failed step so its datetime matches Timeline.
 */
const BACKEND_STATE_TO_PILL = {
  REQUESTED: "validation",
  PRECHECK_RUNNING: "validation",
  NEEDS_INPUTS: "awaiting_approval",
  DRY_RUN_READY: "awaiting_approval",
  PERMS_DENIED: "awaiting_approval",
  APPROVED: "scheduled",
  IN_PROGRESS: "running",
  COMPLETED: "complete",
  ALREADY_REMEDIATED: "complete",
  FAILED: "complete",
  PRECHECK_FAILED: "complete",
};

const TIMELINE_EVENT_TO_STATE = {
  SUCCEEDED: "COMPLETED",
};

const laterIso = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  const aMs = Date.parse(a);
  const bMs = Date.parse(b);
  if (Number.isNaN(aMs)) return Number.isNaN(bMs) ? a : b;
  if (Number.isNaN(bMs)) return a;
  return bMs >= aMs ? b : a;
};

const normalizeTimelineEvent = (event) => {
  if (event == null || event === "") return "";
  const key = String(event).trim().replace(/[\s-]+/g, "_").toUpperCase();
  return TIMELINE_EVENT_TO_STATE[key] || key;
};

/**
 * One ISO timestamp per LIFECYCLE_STEPS entry.
 * Sources: timeline[].at (mapped via BACKEND_STATE_TO_PILL), created_at
 * (REQUESTED / Validation), dry_run_ready_at (DRY_RUN_READY / Awaiting
 * Approval), and status_entered_at when the current status maps to a pill.
 * Missing pills copy the previous step's timestamp.
 */
export function getLifecycleStepTimestamps({
  timeline,
  created_at,
  dry_run_ready_at,
  status,
  status_entered_at,
} = {}) {
  const latestByPill = {};
  const consider = (pillId, iso) => {
    if (!pillId || iso == null || iso === "") return;
    latestByPill[pillId] = laterIso(latestByPill[pillId], iso);
  };

  consider("validation", created_at);
  consider("awaiting_approval", dry_run_ready_at);
  consider(BACKEND_STATE_TO_PILL[status], status_entered_at);

  for (const ev of timeline || []) {
    const state = normalizeTimelineEvent(ev?.event);
    consider(BACKEND_STATE_TO_PILL[state], ev?.at);
  }

  const timestamps = [];
  let previous = null;
  for (const step of LIFECYCLE_STEPS) {
    const iso = latestByPill[step.id] || previous;
    timestamps.push(iso || null);
    if (iso) previous = iso;
  }
  return timestamps;
}

export function getLifecycleProgress(status) {
  if (!status) {
    return { activeIndex: 0, terminal: null, terminalLabel: null };
  }

  if (TERMINAL_CANCELLED.has(status)) {
    return {
      activeIndex: 0,
      terminal: "cancelled",
      terminalLabel: status === "ABANDONED" ? "Abandoned" : "Cancelled",
    };
  }

  if (TERMINAL_FAILED.has(status)) {
    const completeIdx = LIFECYCLE_STEPS.findIndex((s) => s.id === "complete");
    return {
      activeIndex: completeIdx >= 0 ? completeIdx : LIFECYCLE_STEPS.length - 1,
      terminal: "failed",
      terminalLabel: "Failed",
    };
  }

  const completeStep = LIFECYCLE_STEPS.find((s) => s.id === "complete");
  if (completeStep?.statuses.includes(status)) {
    return {
      activeIndex: LIFECYCLE_STEPS.length,
      terminal: "success",
      terminalLabel: null,
    };
  }

  const activeIndex = LIFECYCLE_STEPS.findIndex((step) => step.statuses.includes(status));
  return {
    activeIndex: activeIndex >= 0 ? activeIndex : 0,
    terminal: null,
    terminalLabel: null,
  };
}
